import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { coachServiceFixed as coachService } from '@/services/coachServiceFixed';
import { useTeams } from '@/hooks/useTeams';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { EventBasicFields } from './EventBasicFields';
import { EventDateTimeFields } from './EventDateTimeFields';
import { EventLocationDescriptionFields } from './EventLocationDescriptionFields';

interface CreateEventModalProps {
  open: boolean;
  onClose: () => void;
  onEventCreated: () => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ open, onClose, onEventCreated }) => {
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { teams } = useTeams();
  const [loading, setLoading] = useState(false);

  const currentUserId = profile?.id || internalUser?.id;
  const userTeams = teams.filter(team => team.created_by === currentUserId);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'practice' as 'practice' | 'meeting' | 'training',
    event_date: undefined as Date | undefined,
    start_time: '',
    end_time: '',
    location: '',
    team_id: '',
    timezone: 'CST'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId || !formData.event_date || !formData.title.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Calculate end time if not provided (add 2 hours to start time)
      let endTime = formData.end_time;
      if (!endTime) {
        const [hours, minutes] = formData.start_time.split(':');
        const startDate = new Date();
        startDate.setHours(parseInt(hours), parseInt(minutes));
        startDate.setHours(startDate.getHours() + 2);
        endTime = startDate.toTimeString().slice(0, 5);
      }

      const eventData = {
        coach_id: currentUserId,
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        event_type: formData.event_type,
        event_date: format(formData.event_date, 'yyyy-MM-dd'),
        start_time: formData.start_time,
        end_time: endTime,
        location: formData.location?.trim() || null,
        team_id: formData.team_id ? parseInt(formData.team_id) : null,
        participant_count: formData.team_id ? 1 : 0, // At least the coach
        status: 'scheduled'
      };

      // Note: Schedule functionality not yet implemented in coachServiceFixed
      // await coachService.createScheduleItem(eventData);
      toast.success('Event scheduling feature coming soon!');
      onEventCreated();
      onClose();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        event_type: 'practice',
        event_date: undefined,
        start_time: '',
        end_time: '',
        location: '',
        team_id: '',
        timezone: 'CST'
      });
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0d1117] border border-gray-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sports-green">Create New Event</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <EventBasicFields
            title={formData.title}
            eventType={formData.event_type}
            teamId={formData.team_id}
            userTeams={userTeams}
            onTitleChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
            onEventTypeChange={(value) => setFormData(prev => ({ ...prev, event_type: value as any }))}
            onTeamChange={(value) => setFormData(prev => ({ ...prev, team_id: value }))}
          />

          <EventDateTimeFields
            eventDate={formData.event_date}
            startTime={formData.start_time}
            endTime={formData.end_time}
            timezone={formData.timezone}
            onDateChange={(date) => setFormData(prev => ({ ...prev, event_date: date }))}
            onStartTimeChange={(value) => setFormData(prev => ({ ...prev, start_time: value }))}
            onEndTimeChange={(value) => setFormData(prev => ({ ...prev, end_time: value }))}
            onTimezoneChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
          />

          <EventLocationDescriptionFields
            location={formData.location}
            description={formData.description}
            onLocationChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
            onDescriptionChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
          />

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-sports-green text-black hover:bg-green-600"
            >
              {loading ? 'Creating...' : 'Create Event'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEventModal;